import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { Star, X } from "lucide-react";

export default function EmployerCandidates() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/applications");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const openCand = async (candId) => {
    const { data } = await api.get(`/candidates/${candId}`);
    setSelected(data);
  };

  const feedback = async (id, text) => {
    await api.put(`/applications/${id}`, { employer_feedback: text });
    toast.success("Feedback saved");
  };

  const setSaved = async (id, s) => {
    await api.put(`/applications/${id}`, { saved_by_employer: s });
    toast.success(s ? "Saved" : "Removed");
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-2">Employer</div>
        <h1 className="font-display text-3xl font-bold text-navy">Submitted Candidates</h1>
        <p className="text-sm text-slate-500 mt-1">Only candidates officially submitted by your recruiter appear here.</p>
      </div>

      <div data-testid={TID.employerCandidatesTable} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500">
              <th className="p-3">Candidate</th><th className="p-3">Role</th><th className="p-3">Status</th>
              <th className="p-3">Saved</th><th className="p-3">Feedback</th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="p-3 font-medium text-navy cursor-pointer" onClick={() => openCand(a.candidate_id)}>{a.candidate_name}</td>
                <td className="p-3 text-slate-600">{a.job_title}</td>
                <td className="p-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100">{a.status}</span></td>
                <td className="p-3">
                  <button onClick={() => setSaved(a.id, !a.saved_by_employer)} className={a.saved_by_employer ? "text-gold" : "text-slate-300"}>
                    <Star className="w-4 h-4 fill-current" />
                  </button>
                </td>
                <td className="p-3">
                  <input defaultValue={a.employer_feedback} onBlur={e => feedback(a.id, e.target.value)}
                    placeholder="Add feedback…" className="w-full border rounded px-2 py-1 text-sm" />
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No candidates submitted yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full md:w-[520px] bg-white h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div className="font-display text-xl font-semibold text-navy">{selected.user?.name}</div>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="text-slate-600">{selected.headline}</div>
              <div>
                <div className="eyebrow mb-1">Location</div>
                <div>{selected.city}{selected.state ? `, ${selected.state}` : ""}</div>
              </div>
              <div>
                <div className="eyebrow mb-1">Skills</div>
                <div className="flex flex-wrap gap-1">
                  {(selected.skills || []).map(s => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{s}</span>)}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Experience</div>
                {(selected.experience || []).map((e, i) => (
                  <div key={i} className="mb-2">
                    <div className="font-medium text-navy">{e.title} — {e.company}</div>
                    <div className="text-xs text-slate-500">{e.start}–{e.end}</div>
                    <div className="text-slate-600">{e.description}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="eyebrow mb-1">Education</div>
                {(selected.education || []).map((e, i) => (
                  <div key={i} className="text-slate-700">{e.degree} {e.field}, {e.school} ({e.year})</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
